import { useCallback, useEffect, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useFormik } from 'formik'
import { gerarSegredo2fa, validar2fa } from '../core/_requests'
import {
  ERRO_SESSAO_MFA,
  interpretarErroMfa,
  useConcluirLogin,
  useTwoFactorState,
} from '../core/twoFactor'
import { totpSchema } from './validation/mfaSchema'
import { RecoveryCodes } from './RecoveryCodes'
import { MfaSetupInstructions } from './MfaSetupInstructions'
import {
  AuthTrustFooter,
  Button,
  FormCodeInput,
  InfoAlert,
  LoadingIndicator,
} from '../../../components'
import { AuthTokenResponse, MfaSetup } from '../core/_models'
import { ButtonType } from '../../../../_metronic/helpers'

/**
 * Cadastro obrigatório do autenticador, quando a empresa exige o segundo fator e o usuário
 * ainda não tem um. Roda sobre o token de desafio — não há sessão neste ponto do login.
 */
export const TwoFactorSetup = () => {
  const twoFactor = useTwoFactorState()
  const concluirLogin = useConcluirLogin()
  const navigate = useNavigate()

  const [setup, setSetup] = useState<MfaSetup>()
  const [erroSetup, setErroSetup] = useState<string>()
  const [codigos, setCodigos] = useState<string[]>()
  const [autenticado, setAutenticado] = useState<AuthTokenResponse>()
  const [concluindo, setConcluindo] = useState(false)
  const [erroConclusao, setErroConclusao] = useState<string>()

  const desafio = twoFactor?.desafio

  const carregarSetup = useCallback(async () => {
    if (!desafio) return
    setErroSetup(undefined)
    try {
      setSetup(await gerarSegredo2fa({ token: desafio }))
    } catch {
      setErroSetup('Não foi possível iniciar o cadastro. Faça o login novamente.')
    }
  }, [desafio])

  useEffect(() => {
    carregarSetup()
  }, [carregarSetup])

  /** Devolve a mensagem de erro em vez de lançar, para que o botão nunca fique travado. */
  const entrar = async (auth: AuthTokenResponse): Promise<string | undefined> => {
    setConcluindo(true)
    try {
      await concluirLogin(auth)
      return undefined
    } catch {
      return ERRO_SESSAO_MFA
    } finally {
      setConcluindo(false)
    }
  }

  const formik = useFormik({
    initialValues: { codigo: '' },
    validationSchema: totpSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setSubmitting(true)
      setStatus(undefined)
      try {
        let auth: AuthTokenResponse
        try {
          auth = await validar2fa({ token: desafio!, codigo: values.codigo })
        } catch (error) {
          const { mensagem, desafioEncerrado } = interpretarErroMfa(
            error,
            'Código inválido. Confira o aplicativo e tente de novo.'
          )
          if (desafioEncerrado) navigate('/auth/login', { replace: true })
          else setStatus(mensagem)
          return
        }

        if (!auth.codigosRecuperacao?.length) {
          setStatus(await entrar(auth))
          return
        }

        setAutenticado(auth)
        setCodigos(auth.codigosRecuperacao)
      } finally {
        setSubmitting(false)
      }
    },
  })

  if (!twoFactor) return <Navigate to='/auth/login' replace />

  if (codigos && autenticado) {
    return (
      <div className='card-body'>
        <div className='text-start mb-10'>
          <h1 className='text-dark mb-3 fs-2x'>Verificação ativada</h1>
        </div>

        {erroConclusao && (
          <InfoAlert variant='danger' showIcon={false}>
            {erroConclusao}
          </InfoAlert>
        )}

        <RecoveryCodes
          codigos={codigos}
          isSubmitting={concluindo}
          confirmLabel='Entrar'
          onConfirm={async () => setErroConclusao(await entrar(autenticado))}
        />
      </div>
    )
  }

  return (
    <form onSubmit={formik.handleSubmit} noValidate autoComplete='off'>
      <div className='card-body'>
        <div className='text-start mb-10'>
          <h1 className='text-dark mb-3 fs-2x'>Configure a verificação em duas etapas</h1>
          <div className='text-gray-700 fw-semibold fs-3'>
            Sua empresa exige um segundo fator para acessar a plataforma.
          </div>
        </div>

        {erroSetup && (
          <div className='mb-lg-15 alert alert-danger' role='alert'>
            <div className='alert-text font-weight-bold'>{erroSetup}</div>
          </div>
        )}

        {!setup && !erroSetup && (
          <div className='text-center py-10'>
            <LoadingIndicator />
          </div>
        )}

        {setup && (
          <>
            <MfaSetupInstructions setup={setup} className='mb-8' />

            {formik.status && (
              <div className='mb-lg-15 alert alert-danger' role='alert'>
                <div className='alert-text font-weight-bold'>{formik.status}</div>
              </div>
            )}

            <FormCodeInput
              formik={formik}
              name='codigo'
              label='Código de verificação'
              className='mb-8'
              isRequired
            />

            <div className='d-flex flex-column gap-3'>
              <Button
                type={ButtonType.Submit}
                className='btn-primary btn-auth-full'
                disabled={formik.isSubmitting}
              >
                {formik.isSubmitting ? <LoadingIndicator /> : 'Ativar e entrar'}
              </Button>
              <Button
                onClick={() => navigate('/auth/login')}
                className='btn-secondary btn-auth-full'
                disabled={formik.isSubmitting}
              >
                Cancelar
              </Button>
            </div>
          </>
        )}

        <AuthTrustFooter />
      </div>
    </form>
  )
}
